import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import container from 'src/container';
import { PublicInfoScreen as PublicInfoScreenModel } from 'src/domain/infoScreen';

const {
    cradle: { munduClient },
} = container;

export const PublicInfoScreen = () => {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();
    const [infoScreen, setInfoScreen] = useState<PublicInfoScreenModel>();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [error, setError] = useState<string>();

    const activeSlides = infoScreen?.slides ?? [];
    const currentSlide = activeSlides[currentIndex % activeSlides.length];
    const durationMs =
        Math.max(infoScreen?.slideDurationSeconds ?? 10, 3) * 1000;

    const loadInfoScreen = useCallback(async () => {
        try {
            const nextInfoScreen = await munduClient.getPublicInfoScreen(token);
            setInfoScreen(nextInfoScreen);
            setError(undefined);
        } catch (_error) {
            setError(t('modules.infoScreen.publicNotFound'));
        }
    }, [t, token]);

    useEffect(() => {
        document.title = 'Info screen';

        const existingMeta = document.querySelector(
            'meta[name="robots"]',
        ) as HTMLMetaElement | null;
        const meta = existingMeta ?? document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex,nofollow';
        if (!existingMeta) {
            document.head.appendChild(meta);
        }
    }, []);

    useEffect(() => {
        loadInfoScreen();
        const intervalId = window.setInterval(loadInfoScreen, 30000);
        return () => window.clearInterval(intervalId);
    }, [loadInfoScreen]);

    useEffect(() => {
        setCurrentIndex(0);
    }, [infoScreen?.updatedDate]);

    useEffect(() => {
        if (activeSlides.length <= 1) return;

        const timeoutId = window.setTimeout(() => {
            setCurrentIndex(index => (index + 1) % activeSlides.length);
        }, durationMs);

        return () => window.clearTimeout(timeoutId);
    }, [activeSlides.length, currentIndex, durationMs]);

    const fallbackTitle = useMemo(
        () =>
            infoScreen?.organizationDisplayName ||
            infoScreen?.organizationName ||
            'Mundu',
        [infoScreen?.organizationDisplayName, infoScreen?.organizationName],
    );

    if (error) {
        return (
            <div className="h-screen w-screen bg-woodyBrown text-white flex items-center justify-center">
                <p className="text-3xl">{error}</p>
            </div>
        );
    }

    if (!infoScreen) {
        return (
            <div className="h-screen w-screen bg-woodyBrown text-white flex items-center justify-center">
                <p className="text-3xl">
                    {t('modules.infoScreen.publicLoading')}
                </p>
            </div>
        );
    }

    if (!currentSlide) {
        return (
            <div className="h-screen w-screen bg-woodyBrown text-white flex flex-col items-center justify-center text-center px-10">
                <h1 className="text-6xl font-logo mb-5">{fallbackTitle}</h1>
                <p className="text-3xl opacity-70">
                    {t('modules.infoScreen.publicNoSlides')}
                </p>
            </div>
        );
    }

    return (
        <div className="h-screen w-screen overflow-hidden bg-black">
            <img
                key={currentSlide.id}
                src={currentSlide.imageUrl}
                alt=""
                className={cx(
                    'h-full w-full animate-fade-in-fast',
                    currentSlide.fit === 'contain'
                        ? 'object-contain'
                        : 'object-cover',
                )}
            />
        </div>
    );
};
